/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// ============================================================================
// UNIVERSAL REALTIME MANAGER - Singleton Pattern for All Stores
// ============================================================================

import { getSupabaseClient } from "@/lib/supabase/client";
import { logger } from "@/lib/utils/logger";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { createEventCoalescer, type EventCoalescer } from "./event-coalescer";
import { realtimeMetrics } from "./realtime-metrics";

interface RealtimeSubscription {
  channel: RealtimeChannel;
  organizationId: string;
  table: string;
  onUpdate: (payload: unknown) => void;
  coalescer?: EventCoalescer<unknown>;
  useCoalescing: boolean;
}

class RealtimeManager {
  private static instance: RealtimeManager;
  private subscriptions = new Map<string, RealtimeSubscription>();
  private referenceCounts = new Map<string, number>();
  private reconnectTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private reconnectAttempts = new Map<string, number>();
  private readonly maxReconnectAttempts = 5;
  private readonly baseReconnectDelay = 1000; // 1 second
  private readonly maxReconnectDelay = 30000; // 30 seconds

  static getInstance(): RealtimeManager {
    if (!RealtimeManager.instance) {
      RealtimeManager.instance = new RealtimeManager();
    }
    return RealtimeManager.instance;
  }

  subscribe(
    organizationId: string,
    table: string,
    onUpdate: (payload: unknown) => void,
    options?: {
      useCoalescing?: boolean;
      debounceMs?: number;
      throttleMs?: number;
      maxBatchSize?: number;
    },
  ): void {
    // Validate organization ID
    if (!organizationId || typeof organizationId !== "string") {
      logger.error(`[REALTIME] Invalid organizationId for ${table}`);
      return;
    }

    const key = `${organizationId}_${table}`;
    const useCoalescing = options?.useCoalescing ?? true;

    // If already subscribed, just increment reference count
    if (this.subscriptions.has(key)) {
      const currentCount = this.referenceCounts.get(key) ?? 0;
      this.referenceCounts.set(key, currentCount + 1);
      realtimeMetrics.recordSubscription(organizationId, table);
      return;
    }

    try {
      // Create coalescer if enabled
      let coalescer: EventCoalescer<unknown> | undefined;
      if (useCoalescing) {
        coalescer = createEventCoalescer({
          organizationId,
          table,
          debounceMs: options?.debounceMs ?? 100,
          throttleMs: options?.throttleMs ?? 1000,
          maxBatchSize: options?.maxBatchSize ?? 50,
          handler: async (coalescedEvent) => {
            // Process all events in the batch
            for (const event of coalescedEvent.events) {
              try {
                onUpdate(event.payload);
              } catch (error) {
                logger.error(
                  `[REALTIME] Error in coalesced update callback for ${table}:`,
                  error,
                );
                realtimeMetrics.recordError(organizationId, table);
              }
            }
          },
        });
      }

      // Create wrapped handler
      const wrappedHandler = (payload: unknown) => {
        try {
          // Determine event type from payload
          const eventType = this.getEventType(payload);

          if (coalescer) {
            coalescer.add(payload, eventType);
          } else {
            onUpdate(payload);
            realtimeMetrics.recordEvent(
              organizationId,
              table,
              eventType,
              false,
              false,
            );
          }
        } catch (error) {
          logger.error(
            `[REALTIME] Error processing event for ${table}:`,
            error,
          );
          realtimeMetrics.recordError(organizationId, table);
        }
      };

      // Create new subscription with org-scoped filter
      const channel = getSupabaseClient()
        .channel(`${table}_changes_${organizationId}_${Date.now()}`) // Unique channel name
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: table,
            filter: `organization_id=eq.${organizationId}`, // Org-scoped filter
          },
          wrappedHandler,
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            logger.info(
              `🔗 [REALTIME] Successfully subscribed to ${table} for organization ${organizationId}`,
            );
            this.reconnectAttempts.delete(key);
            realtimeMetrics.recordSubscription(organizationId, table);
          } else if (status === "CHANNEL_ERROR") {
            logger.warn(
              `⚠️ [REALTIME] Failed to subscribe to ${table} for organization ${organizationId} - continuing without realtime`,
            );
            realtimeMetrics.recordError(organizationId, table);
            this.scheduleReconnect(
              key,
              organizationId,
              table,
              onUpdate,
              options,
            );
          } else if (status === "TIMED_OUT") {
            logger.warn(
              `⚠️ [REALTIME] Subscription timeout for ${table} for organization ${organizationId}`,
            );
            realtimeMetrics.recordError(organizationId, table);
            this.scheduleReconnect(
              key,
              organizationId,
              table,
              onUpdate,
              options,
            );
          } else if (status === "CLOSED") {
            logger.info(
              `🔗 [REALTIME] Subscription closed for ${table} for organization ${organizationId}`,
            );
            this.reconnectAttempts.delete(key);
          }
        });

      this.subscriptions.set(key, {
        channel,
        organizationId,
        table,
        onUpdate,
        coalescer,
        useCoalescing,
      });
      this.referenceCounts.set(key, 1);
      realtimeMetrics.recordSubscription(organizationId, table);
    } catch (error) {
      logger.warn(
        `⚠️ [REALTIME] Error setting up subscription for ${table}:`,
        error,
      );
      realtimeMetrics.recordError(organizationId, table);
      // Continue without realtime - the app will still work
    }
  }

  private getEventType(
    payload: unknown,
  ): "INSERT" | "UPDATE" | "DELETE" | "UNKNOWN" {
    if (payload && typeof payload === "object") {
      const p = payload as Record<string, unknown>;
      if (p.eventType) {
        const et = String(p.eventType).toUpperCase();
        if (et === "INSERT" || et === "UPDATE" || et === "DELETE") {
          return et as "INSERT" | "UPDATE" | "DELETE";
        }
      }
      // Check for new/old pattern
      if (p.new && !p.old) return "INSERT";
      if (p.new && p.old) return "UPDATE";
      if (!p.new && p.old) return "DELETE";
    }
    return "UNKNOWN";
  }

  private scheduleReconnect(
    key: string,
    organizationId: string,
    table: string,
    onUpdate: (payload: unknown) => void,
    options?: {
      useCoalescing?: boolean;
      debounceMs?: number;
      throttleMs?: number;
      maxBatchSize?: number;
    },
  ): void {
    // Prevent cascade: clear any existing reconnect timer
    const existingTimer = this.reconnectTimers.get(key);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const attempts = this.reconnectAttempts.get(key) ?? 0;
    if (attempts >= this.maxReconnectAttempts) {
      logger.error(
        `❌ [REALTIME] Max reconnect attempts reached for ${table} (${organizationId})`,
      );
      return;
    }

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, attempts) + Math.random() * 1000,
      this.maxReconnectDelay,
    );

    logger.info(
      `🔄 [REALTIME] Scheduling reconnect for ${table} (${organizationId}) in ${delay}ms (attempt ${attempts + 1}/${this.maxReconnectAttempts})`,
    );

    const timer = setTimeout(() => {
      this.reconnectTimers.delete(key);
      this.reconnectAttempts.set(key, attempts + 1);
      realtimeMetrics.recordReconnect(organizationId, table);

      // Unsubscribe first to clean up
      const subscription = this.subscriptions.get(key);
      if (subscription) {
        try {
          getSupabaseClient().removeChannel(subscription.channel);
        } catch (error) {
          logger.warn(
            `[REALTIME] Error removing channel during reconnect:`,
            error,
          );
        }
        this.subscriptions.delete(key);
      }

      // Resubscribe
      this.subscribe(organizationId, table, onUpdate, options);
    }, delay);

    this.reconnectTimers.set(key, timer);
  }

  unsubscribe(organizationId: string, table: string): void {
    const key = `${organizationId}_${table}`;
    const currentCount = this.referenceCounts.get(key) ?? 0;

    if (currentCount <= 1) {
      // Last reference, actually unsubscribe
      const subscription = this.subscriptions.get(key);
      if (subscription) {
        try {
          // Clean up coalescer
          if (subscription.coalescer) {
            subscription.coalescer.destroy();
          }

          // Clear reconnect timer
          const reconnectTimer = this.reconnectTimers.get(key);
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            this.reconnectTimers.delete(key);
          }

          // Remove channel
          getSupabaseClient().removeChannel(subscription.channel);
          logger.info(
            `🔗 [REALTIME] Unsubscribed from ${table} for organization ${organizationId}`,
          );
          realtimeMetrics.recordUnsubscription(organizationId, table);
        } catch (error) {
          logger.warn(
            `⚠️ [REALTIME] Error unsubscribing from ${table}:`,
            error,
          );
          realtimeMetrics.recordError(organizationId, table);
        }
        this.subscriptions.delete(key);
        this.referenceCounts.delete(key);
        this.reconnectAttempts.delete(key);
      }
    } else {
      // Decrement reference count
      this.referenceCounts.set(key, currentCount - 1);
      realtimeMetrics.recordUnsubscription(organizationId, table);
    }
  }

  unsubscribeAll(): void {
    // Clean up all subscriptions
    for (const [key, subscription] of this.subscriptions.entries()) {
      try {
        // Clean up coalescer
        if (subscription.coalescer) {
          subscription.coalescer.destroy();
        }

        // Clear reconnect timer
        const reconnectTimer = this.reconnectTimers.get(key);
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }

        getSupabaseClient().removeChannel(subscription.channel);
        logger.info(
          `🔗 [REALTIME] Unsubscribed from ${subscription.table} for organization ${subscription.organizationId}`,
        );
        realtimeMetrics.recordUnsubscription(
          subscription.organizationId,
          subscription.table,
        );
      } catch (error) {
        logger.warn(
          `⚠️ [REALTIME] Error unsubscribing from ${subscription.table}:`,
          error,
        );
        realtimeMetrics.recordError(
          subscription.organizationId,
          subscription.table,
        );
      }
    }
    this.subscriptions.clear();
    this.referenceCounts.clear();
    this.reconnectTimers.clear();
    this.reconnectAttempts.clear();
  }

  getActiveSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  getMetrics(organizationId?: string) {
    return realtimeMetrics.getMetrics(organizationId);
  }

  getMetricsByOrg() {
    return realtimeMetrics.getMetricsByOrg();
  }
}

export const realtimeManager = RealtimeManager.getInstance();
